import { useInfiniteQuery } from '@tanstack/react-query';
import { DeviceType, Profile, ProfileType, UserProfileEntryProps } from '@ixicare/resources';
import {
  formatDate,
  Button,
  Table,
  Link,
  BatteryIndicator,
  IconButton,
  Search,
  formatDateTime,
  getCurrentDate,
} from '@ixicare/ui';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { useOnReachBottom } from './utils';
import styled from 'styled-components';
import OfflineDevice from '~components/OfflineDevice/OfflineDevice';
import styles from './ProfileTable.module.css';
import { NoPermissionModal } from '~containers/Permissions/NoPermissionsModal';

export function ProfileTable({
  profileType,
  enableBatteryLegend,
  editPermissions,
  deletePermissions,
}: {
  profileType: ProfileType;
  enableBatteryLegend?: boolean;
  editPermissions?: boolean;
  deletePermissions?: boolean;
}) {
  const columns = React.useMemo(() => {
    const arr = [t('profiles.table.firstName'), t('profiles.table.lastName'), t('profiles.table.created')];
    if (profileType === ProfileType.HealthcareProvider) {
      arr.push(t('profiles.table.role'));
    }
    if (profileType === ProfileType.Resident) {
      arr.push(t('profiles.table.device'));
      arr.push(t('profiles.table.deviceUpdatedAt'));
    }
    arr.push(t('profiles.table.edit'));
    return arr;
  }, [profileType]);

  const perPage = 20;

  const [orderBy, setOrderBy] = React.useState('created');
  const [ascOrder, setAscOrder] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [showLegend, setShowLegend] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);

  const queryFn = ({ pageParam = 1 }) => {
    if (search) {
      return Profile.getProfiles({
        page: pageParam,
        perPage,
        profileType,
        search: search ? search : undefined,
      });
    }
    return Profile.getProfiles({
      page: pageParam,
      perPage,
      orderBy,
      order: ascOrder ? 'asc' : 'desc',
      profileType,
    });
  };

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useInfiniteQuery({
    queryKey: ['profiles', profileType, orderBy, ascOrder, search],
    queryFn,
    refetchOnMount: true,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.length === perPage ? pages.length + 1 : undefined;
    },
  });

  useOnReachBottom(fetchNextPage, true);

  const rows = React.useMemo(() => {
    return data?.pages.flatMap((x) => x);
  }, [data]);

  const columnSortKeys = ['first_name', 'last_name', 'created'];

  const handleSort = (index: number) => {
    const key = columnSortKeys[index];
    if (orderBy === key) {
      setAscOrder((x) => !x);
    }
    if (key) {
      setOrderBy(key);
    }
  };

  const { confirmation } = useStores();
  const navigate = useNavigate();

  const handleDelete = (profile: UserProfileEntryProps) => {
    confirmation.show({
      title:
        profile.profileType === ProfileType.Resident
          ? t('residentDetail.confirmDelete.title')
          : t('healthcareProviderDetail.confirmDelete.title'),
      message:
        profile.profileType === ProfileType.Resident
          ? t('residentDetail.confirmDelete.message')
          : t('healthcareProviderDetail.confirmDelete.message'),
      onConfirm: () => {
        Profile.archiveProfile(profile.uuid)
          .then(() => {
            refetch();
          })
          .catch((e) => {
            console.error(e);
            refetch();
          });
      },
      onCancel: () => {},
    });
  };

  const orderColumn = search ? -1 : columnSortKeys.indexOf(orderBy);
  console.log(profileType);

  return (
    <>
      <div className="row mb-30">
        <div className={`${styles.Residents_header}`}>
          <Table.Col className={`${styles.Residents_search_container}`}>
            <Search value={search} onSearchChange={setSearch} placeholder={t('profiles.filters.search')} />
          </Table.Col>
          {enableBatteryLegend && (
            <Table.Col className={`${styles.Legend_btn_container}`}>
              <button onClick={() => setShowLegend(!showLegend)}>{t('legend.overlay.title').toLowerCase()}</button>
            </Table.Col>
          )}
        </div>
      </div>

      {showLegend && (
        <section className={`${styles.Legend_container}`}>
          <Table.Head className={`${styles.Legend_header}`}>
            <Table.Row>
              <Table.HeadCol>
                <p>
                  <IconButton icon="cross" />
                </p>
              </Table.HeadCol>
              <Table.HeadCol>
                <p>*!</p>
              </Table.HeadCol>
              <Table.HeadCol>
                <p>Last Seen</p>
              </Table.HeadCol>
            </Table.Row>
          </Table.Head>
          <Table.Body className={`${styles.Legend_body}`}>
            <Table.Row>
              <Table.Col>
                <p>{t('profiles.legend.notAvailable.description')}</p>
              </Table.Col>
              <Table.Col>
                <p>{t('profiles.legend.brokenBattery.description')}</p>
              </Table.Col>
              <Table.Col>
                <p>{t('profiles.legend.lastSeen.description')}</p>
              </Table.Col>
            </Table.Row>
          </Table.Body>
        </section>
      )}
      <Table>
        <Table.Head>
          <Table.Row>
            {columns.map((text, i) => (
              <Table.HeadCol onClick={() => handleSort(i)} key={i}>
                <span>{text}</span>
                {orderColumn === i && <i className={ascOrder ? 'icon-chevron-up' : 'icon-chevron-down'}></i>}
              </Table.HeadCol>
            ))}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {rows?.map((resident) => (
            <Table.Row key={`profile-${resident.uuid}`}>
              <Table.Col>
                {profileType === ProfileType.Resident ? (
                  <Link href={`/residents/${resident.id}`}>{resident.firstName}</Link>
                ) : (
                  resident.firstName
                )}
              </Table.Col>
              <Table.Col>
                {profileType === ProfileType.Resident ? (
                  <Link href={`/residents/${resident.id}`}>{resident.lastName}</Link>
                ) : (
                  resident.lastName
                )}
              </Table.Col>
              <Table.Col>{formatDate(resident.created)}</Table.Col>
              {profileType === ProfileType.Resident && (
                <>
                  <Table.Col>
                    <DeviceFieldRow>
                      {resident?.ownedDevice?.deviceType && (
                        <DeviceImg src={`/assets/${resident.ownedDevice.deviceType.toLowerCase()}@2x.png`} />
                      )}
                      {resident.ownedDevice?.hoursWithLowBatteryInLast24h &&
                      resident.ownedDevice?.maxUptimeInLast24h &&
                      resident.ownedDevice?.hoursWithLowBatteryInLast24h >= 6 &&
                      resident.ownedDevice.maxUptimeInLast24h / 3600 >= 4 ? (
                        <>
                          <p style={{ fontWeight: 'bolder', marginRight: '.5rem', color: '#ff2e2e' }}>
                            <b>!*</b>
                          </p>
                          <BatteryIndicator
                            inaccurate={resident?.ownedDevice?.deviceType !== DeviceType.WATCHERR}
                            percentage={0}
                          />
                        </>
                      ) : typeof resident.ownedDevice?.batteryLevel === 'number' ? (
                        <BatteryIndicator
                          inaccurate={resident?.ownedDevice?.deviceType !== DeviceType.WATCHERR}
                          percentage={resident.ownedDevice.batteryLevel}
                        />
                      ) : (
                        <IconButton icon="cross" />
                      )}
                    </DeviceFieldRow>
                  </Table.Col>
                  <Table.Col>
                    {resident.ownedDevice?.status?.updatedOn ? (
                      formatDate(resident.ownedDevice?.status?.updatedOn) === getCurrentDate() ? (
                        formatDateTime(resident.ownedDevice?.status?.updatedOn)
                      ) : (
                        <OfflineDevice date={resident.ownedDevice?.status?.updatedOn} />
                      )
                    ) : (
                      <IconButton icon="cross" />
                    )}
                  </Table.Col>
                </>
              )}
              {profileType === ProfileType.HealthcareProvider ? (
                <>
                  <Table.Col>
                    <p>{resident.teams?.length != 0 ? t('global.team.admin') : t('global.team.staff')}</p>
                  </Table.Col>
                </>
              ) : (
                <></>
              )}
              <Table.Col>
                {editPermissions ? (
                  <IconButton
                    icon="edit"
                    onClick={() =>
                      navigate(
                        profileType === ProfileType.Resident
                          ? `/residents/${resident.id}/edit`
                          : `/healthcare-providers/${resident.id}/edit`
                      )
                    }
                  />
                ) : (
                  <IconButton icon="lock" onClick={() => setShowModal(true)} />
                )}
                {deletePermissions ? (
                  <IconButton icon="delete" onClick={() => handleDelete(resident)} />
                ) : (
                  <IconButton icon="lock" onClick={() => setShowModal(true)} />
                )}
              </Table.Col>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {hasNextPage && !error && (
        <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
          Load more
        </Button>
      )}
      {showModal && <NoPermissionModal onClose={() => setShowModal(false)} />}
    </>
  );
}

const DeviceFieldRow = styled.div`
  display: flex;
  align-items: center;
`;

const DeviceImg = styled.img`
  height: 20px;
  width: 20px;
  margin-right: 15px;
`;
